// Blossom.js
import { useEffect } from 'react';

class Petal {
  constructor(config = {}) {
    this.customClass = config.customClass || '';
    this.x = config.x || 0;
    this.y = config.y || 0;
    this.z = config.z || 0;
    this.xSpeedVariation = config.xSpeedVariation || 0;
    this.ySpeed = config.ySpeed || 0;
    this.rotation = {
      axis: 'X',
      value: 0,
      speed: 0,
      x: 0
    };

    this.el = document.createElement('div');
    this.el.className = 'petal ' + this.customClass;
  }
}

class BlossomScene {
  constructor({ id, petalsTypes, numPetals = 40, gravity = 0.8, windMaxSpeed = 10 }) {
    this.container = document.getElementById(id);
    if (!this.container) throw new Error('[id] not found');
    
    this.placeholder = document.createElement('div');
    this.petals = [];
    this.numPetals = numPetals;
    this.petalsTypes = petalsTypes;
    this.gravity = gravity;
    this.windMaxSpeed = windMaxSpeed;
    this.windMagnitude = 0.2;
    this.windDuration = 0;
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.timer = 0;

    this.placeholder.style.transformStyle = 'preserve-3d';
    this.placeholder.style.width = '100%';
    this.placeholder.style.height = '100%';
    this.container.appendChild(this.placeholder);
    
    this.createPetals();
    this.animationFrame = requestAnimationFrame(this.updateFrame.bind(this));
  }

  resetPetal(petal) {
    petal.x = this.width/2 + (Math.random() * this.width - this.width/2);
    petal.y = -50;
    petal.z = Math.random() * 200;

    if (petal.x > this.width) {
      petal.x = this.width + 50;
      petal.y = Math.random() * this.height / 2;
    }

    petal.rotation.speed = Math.random() * 10;
    const randomAxis = Math.random();
    if (randomAxis > 0.5) {
      petal.rotation.axis = 'X';
    } else if (randomAxis > 0.25) {
      petal.rotation.axis = 'Y';
      petal.rotation.x = Math.random() * 180 + 90;
    } else {
      petal.rotation.axis = 'Z';
      petal.rotation.x = Math.random() * 360 - 180;
      petal.rotation.speed = Math.random() * 3;
    }

    petal.xSpeedVariation = Math.random() * 0.8 - 0.4;
    petal.ySpeed = Math.random() + this.gravity;

    return petal;
  }

  calculateWindSpeed(t, y) {
    const a = this.windMagnitude / 2 * (this.height - 2 * y / 3) / this.height;
    return a * Math.sin(2 * Math.PI / this.windDuration * t + (3 * Math.PI / 2)) + a;
  }

  updatePetal(petal) {
    const petalWindSpeed = this.calculateWindSpeed(this.timer, petal.y);
    const xSpeed = petalWindSpeed + petal.xSpeedVariation;

    petal.x -= xSpeed;
    petal.y += petal.ySpeed;
    petal.rotation.value += petal.rotation.speed;

    let transform = `translateX(${petal.x}px) translateY(${petal.y}px) translateZ(${petal.z}px) rotate${petal.rotation.axis}(${petal.rotation.value}deg)`;
    if (petal.rotation.axis !== 'X') {
      transform += ` rotateX(${petal.rotation.x}deg)`;
    }
    petal.el.style.transform = transform;

    if (petal.x < -10 || petal.y > this.height + 10) {
      this.resetPetal(petal);
    }
  }

  updateWind() {
    this.windMagnitude = Math.random() * this.windMaxSpeed;
    this.windDuration = this.windMagnitude * 50 + (Math.random() * 20 - 10);
  }

  createPetals() {
    for (let i = 0; i < this.numPetals; i++) {
      const petalType = this.petalsTypes[Math.floor(Math.random() * this.petalsTypes.length)];
      const petal = new Petal({ customClass: petalType.customClass });
      this.resetPetal(petal);
      this.petals.push(petal);
      this.placeholder.appendChild(petal.el);
    }
  }

  updateFrame() {
    if (this.timer === this.windDuration) {
      this.updateWind();
      this.timer = 0;
    }

    this.petals.forEach(petal => this.updatePetal(petal));
    this.timer++;
    this.animationFrame = requestAnimationFrame(this.updateFrame.bind(this));
  }

  cleanup() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.container.innerHTML = '';
  }
}

const BlossomEffect = () => {
  useEffect(() => {
    const petalsTypes = [
      { customClass: 'petal-style1' },
      { customClass: 'petal-style2' },
      { customClass: 'petal-style3' },
      { customClass: 'petal-style4' }
    ].map(config => new Petal(config));

    const scene = new BlossomScene({
      id: 'blossom_container',
      petalsTypes,
      numPetals: 30,
      windMaxSpeed: 3
    });

    return () => scene.cleanup();
  }, []);

  return (
    <div id="blossom_container" className="absolute inset-0 pointer-events-none z-[100]" />
  );
};

export default BlossomEffect;